import apiClient from '../helpers/apiClient'

/**
 * Member Taxonomy service — written against the WP-T1/T2 backend contract (nobilis-social `taxonomy`
 * app, PRs #44/#45). apiClient.baseURL already includes `/api/v1`, so paths are relative to that.
 *
 * ⚠️ BLOCKED ON TAXONOMY BACKEND DEPLOY: #44's migration isn't applied to dev yet, so these calls are
 * unverified against a live server. Responses are camelCased by djangorestframework_camel_case.
 *
 * Contract:
 *  - MemberCategory: { id, name, description, isActive } — admin-extensible catalog (5 seeds).
 *  - MemberCategoryAssignment (self/staff read): { id, member, category (id), categoryName, isPrimary,
 *    status ('member_proposed' | 'staff_confirmed'), confirmedBy, createdAt, updatedAt }.
 *  - Self-select is a SUGGESTION (proposals are member_proposed; never downgrade a confirmed one).
 */

export const MEMBER_CATEGORIES_ENDPOINT = '/taxonomy/categories/' // MemberCategory catalog
export const MY_CATEGORIES_ENDPOINT = '/taxonomy/me/categories/' // caller's own assignments

export type MemberCategory = {
  id: number
  name: string
  description?: string
  isActive?: boolean
}

export type AssignmentStatus = 'member_proposed' | 'staff_confirmed'

export type MemberCategoryAssignment = {
  id: number
  member: number
  category: number
  categoryName: string
  isPrimary: boolean
  status: AssignmentStatus
  confirmedBy?: number | null
  createdAt?: string
  updatedAt?: string
}

const unwrap = <T>(data: any): T[] => (data && data.results ? data.results : data) ?? []

// --- catalog (any authenticated user) ------------------------------------------------
export const getMemberCategories = async (): Promise<MemberCategory[]> => {
  // PR-I-77: pedimos catalogo activo y ordenado explicitamente. El backend
  // (taxonomy.MemberCategoryViewSet) ya filtra `is_active=True` para
  // no-admins y ordena por `order`, pero mandar los parametros blinda a
  // futuros cambios del default.
  // PR-I-79: pedimos limit=200 porque DRF pagina por default a 5 y el catalogo
  // ya tiene 6 activas, recortando "Writers".
  const {data} = await apiClient.get(MEMBER_CATEGORIES_ENDPOINT, {
    params: {isActive: true, ordering: 'order', limit: 200},
  })
  return unwrap<MemberCategory>(data)
}

// --- member self-select (own record) -------------------------------------------------
export const getMyCategories = async (): Promise<MemberCategoryAssignment[]> => {
  const {data} = await apiClient.get(MY_CATEGORIES_ENDPOINT)
  return unwrap<MemberCategoryAssignment>(data)
}

/**
 * Propose (upsert) a category on the caller's own profile → status=member_proposed. Passing
 * isPrimary=true also makes it the single primary. Re-POSTing an existing category with isPrimary
 * flips the primary without downgrading a staff-confirmed row (backend semantics).
 */
export const proposeMyCategory = async (
  categoryId: number,
  isPrimary = false
): Promise<MemberCategoryAssignment> => {
  const {data} = await apiClient.post(MY_CATEGORIES_ENDPOINT, {category: categoryId, isPrimary})
  return data
}

/** Remove one of the caller's own selections — keyed by CATEGORY id (backend route). */
export const removeMyCategory = async (categoryId: number): Promise<void> => {
  await apiClient.delete(`${MY_CATEGORIES_ENDPOINT}${categoryId}/`)
}

/** Mark a category as the caller's primary (qualifying) one — via propose(isPrimary=true). */
export const setMyPrimaryCategory = async (categoryId: number): Promise<MemberCategoryAssignment> =>
  proposeMyCategory(categoryId, true)

// --- staff management (WP-T4, IsAdminRole) -------------------------------------------
export const ADMIN_ASSIGNMENTS_ENDPOINT = '/taxonomy/admin/assignments/'

/** All of a member's assignments (proposed + confirmed) for the staff queue. */
export const getMemberAssignments = async (
  memberId: number | string
): Promise<MemberCategoryAssignment[]> => {
  // PR-I-79: limit=200 para evitar el corte de paginacion DRF (5 default).
  const {data} = await apiClient.get(
    `${ADMIN_ASSIGNMENTS_ENDPOINT}?member=${memberId}&limit=200`
  )
  return unwrap<MemberCategoryAssignment>(data)
}

/** Staff assign a member to a category (upsert; defaults staff_confirmed). */
export const assignMemberCategory = async (
  memberId: number | string,
  categoryId: number,
  opts: {isPrimary?: boolean; status?: AssignmentStatus} = {}
): Promise<MemberCategoryAssignment> => {
  const {data} = await apiClient.post(ADMIN_ASSIGNMENTS_ENDPOINT, {
    member: Number(memberId),
    category: categoryId,
    isPrimary: opts.isPrimary ?? false,
    ...(opts.status ? {status: opts.status} : {}),
  })
  return data
}

/** Staff confirm a member-proposed assignment → staff_confirmed (public). */
export const confirmAssignment = async (
  assignmentId: number
): Promise<MemberCategoryAssignment> => {
  const {data} = await apiClient.post(`${ADMIN_ASSIGNMENTS_ENDPOINT}${assignmentId}/confirm/`)
  return data
}

/** Staff remove an assignment. */
export const removeAssignment = async (assignmentId: number): Promise<void> => {
  await apiClient.delete(`${ADMIN_ASSIGNMENTS_ENDPOINT}${assignmentId}/`)
}

/** Staff set a member's primary category (upsert with isPrimary=true; keeps it confirmed). */
export const setAssignmentPrimary = async (
  memberId: number | string,
  categoryId: number
): Promise<MemberCategoryAssignment> =>
  assignMemberCategory(memberId, categoryId, {isPrimary: true, status: 'staff_confirmed'})

// --- public read (WP-T5) -------------------------------------------------------------
/** A member's public (staff-confirmed) categories. */
export type PublicMemberCategory = {id: number; name: string; isPrimary: boolean}

export const getMemberPublicCategories = async (
  userId: number | string
): Promise<PublicMemberCategory[]> => {
  const {data} = await apiClient.get(`/taxonomy/members/${userId}/categories/`)
  return unwrap<PublicMemberCategory>(data)
}

/**
 * Category source for the members-screen filter chips. Consumes the taxonomy catalog
 * (/taxonomy/categories/) — the backend already devuelve solo activas para no-admins
 * y ordenadas por `order`. Returns {id, name}.
 *
 * PR-I-77: eliminado el fallback a /members/member-types/ (legacy MemberType) porque
 * ese catalogo no tiene `order` ni `is_active`, y estaba mostrando categorias
 * inactivas y desordenadas (ej. "Athlets") cuando el taxonomy endpoint fallaba.
 * Ante un error del backend nuevo preferimos UI vacia a datos incorrectos.
 */
export const getCategoryChipsWithFallback = async (): Promise<{id: number; name: string}[]> => {
  const cats = await getMemberCategories()
  return cats.map((c) => ({id: c.id, name: c.name}))
}
